'use client';

import { type ReactNode, useRef } from 'react';
import { cn } from '@/lib/utils';

export interface SegmentedItem<T extends string> {
  value: T;
  label: ReactNode;
  icon?: ReactNode;
  disabled?: boolean;
}

/**
 * SegmentedControl / tab list (DESIGN.md §6). Roving focus with arrow keys,
 * Home and End; `role` switches between `tablist` (panels) and `radiogroup`
 * (a value picker).
 */
export function SegmentedControl<T extends string>({
  value,
  onChange,
  items,
  label,
  size = 'md',
  role = 'radiogroup',
  className,
  fill,
  compact = false,
  idPrefix,
}: {
  value: T;
  onChange: (value: T) => void;
  items: SegmentedItem<T>[];
  label: string;
  size?: 'sm' | 'md';
  role?: 'radiogroup' | 'tablist';
  className?: string;
  /** Stretch items to fill the track. */
  fill?: boolean;
  /** Use tighter item spacing when the control sits in a narrow shell. */
  compact?: boolean;
  /** When role=tablist, tab ids are `${idPrefix}-tab-${value}` and panels should use `${idPrefix}-panel-${value}`. */
  idPrefix?: string;
}) {
  const refs = useRef<Array<HTMLButtonElement | null>>([]);
  const enabled = items.map((i, idx) => (i.disabled ? -1 : idx)).filter((i) => i >= 0);

  function move(from: number, delta: number) {
    if (!enabled.length) return;
    const pos = enabled.indexOf(from);
    const next = enabled[(pos + delta + enabled.length) % enabled.length];
    refs.current[next]?.focus();
    onChange(items[next].value);
  }

  const itemRole = role === 'tablist' ? 'tab' : 'radio';

  return (
    <div
      role={role}
      aria-label={label}
      className={cn(
        'inline-flex min-w-0 items-center rounded-control bg-surface-2 p-0.5',
        compact ? 'gap-0' : 'gap-0.5',
        fill && 'flex w-full',
        className
      )}
    >
      {items.map((item, idx) => {
        const selected = item.value === value;

        return (
          <button
            key={item.value}
            ref={(el) => {
              refs.current[idx] = el;
            }}
            type="button"
            role={itemRole}
            id={idPrefix ? `${idPrefix}-tab-${item.value}` : undefined}
            aria-controls={
              idPrefix && role === 'tablist' ? `${idPrefix}-panel-${item.value}` : undefined
            }
            aria-selected={role === 'tablist' ? selected : undefined}
            aria-checked={role === 'radiogroup' ? selected : undefined}
            tabIndex={selected ? 0 : -1}
            disabled={item.disabled}
            onClick={() => onChange(item.value)}
            onKeyDown={(e) => {
              if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
                e.preventDefault();
                move(idx, 1);
              } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
                e.preventDefault();
                move(idx, -1);
              } else if (e.key === 'Home') {
                e.preventDefault();
                refs.current[enabled[0]]?.focus();
                onChange(items[enabled[0]].value);
              } else if (e.key === 'End') {
                e.preventDefault();
                const l = enabled[enabled.length - 1];
                refs.current[l]?.focus();
                onChange(items[l].value);
              }
            }}
            className={cn(
              'inline-flex min-w-0 items-center justify-center whitespace-nowrap rounded-[6px] font-medium',
              compact ? 'gap-1' : 'gap-1.5',
              'transition-[background-color,color,box-shadow] duration-150',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent',
              'disabled:opacity-50',
              compact
                ? 'h-11 px-1 text-[13px] lg:h-7'
                : size === 'sm'
                  ? 'h-11 px-2.5 text-[13px] lg:h-7'
                  : 'h-11 px-3 text-sm lg:h-8',
              fill && 'flex-1',
              selected
                ? 'bg-surface text-ink shadow-[0_1px_2px_rgb(0_0_0/0.12)]'
                : 'text-ink-2 hover:text-ink',
              '[&_svg]:size-4'
            )}
          >
            {item.icon}
            {item.label}
          </button>
        );
      })}
    </div>
  );
}
